import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { withCache } from '@ngneat/cashew';
import { takePromise } from '@y42/util/take-promise';
import { distinctUntilChanged, forkJoin, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import {
  ExportQuery,
  ExportTypeSecretJSONSchema,
  ExportTypeSourceJSONSchema,
  ExportTypeTableJSONSchema,
} from '../../state';
import { ExportService } from '../export/export.service';

@Injectable({ providedIn: 'root' })
export class ExportTypeJSONSchemaHttpService {
  constructor(
    private readonly http: HttpClient,
    private readonly query: ExportQuery,
    private readonly service: ExportService,
  ) {}

  readonly activeExportJSONSchema$: Observable<{
    source: ExportTypeSourceJSONSchema;
    secret: ExportTypeSecretJSONSchema;
    table: ExportTypeTableJSONSchema;
  } | null> = this.query.active$.pipe(
    distinctUntilChanged((a, b) => a?.typeModel.type === b?.typeModel.type),
    switchMap((active) => {
      if (!active) {
        return of(null);
      }
      const sourceJsonURL = this.service.getUrl(active.typeModel.type, 'source');
      const secretJsonURL = this.service.getUrl(active.typeModel.type, 'secret');
      const tableJsonURL = this.service.getUrl(active.typeModel.type, 'table');
      return forkJoin([
        this.http.get<ExportTypeSourceJSONSchema>(sourceJsonURL, {
          context: withCache({
            key: sourceJsonURL,
          }),
        }),
        this.http.get<ExportTypeSecretJSONSchema>(secretJsonURL, { context: withCache({ key: secretJsonURL }) }),
        this.http.get<ExportTypeTableJSONSchema>(tableJsonURL, { context: withCache({ key: tableJsonURL }) }),
      ]).pipe(map(([source, secret, table]) => ({ source, secret, table })));
    }),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  readonly source$ = this.activeExportJSONSchema$.pipe(
    map((jsons) => (jsons ? jsons.source : null)),
    distinctUntilChanged(),
  );

  // used inside Exports to check whether it is valid
  async checkForValidSettings(): Promise<boolean> {
    const activeExport = await takePromise(this.query.active$);
    const source = await takePromise(this.source$);
    if (!source || !source.required) {
      return true;
    }

    return source.required.every((field) => {
      const value = (activeExport?.settings as unknown as Record<string, unknown>)?.[field];
      return Validators.required(new FormControl(value)) === null;
    });
  }
}
